import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ReviewList } from "./reviews/ReviewList";
import type { Tables } from "@/integrations/supabase/types";

interface PropertyReviewsProps {
  propertyId: string;
}

type ReviewWithUser = Tables<"property_reviews"> & {
  profiles: Pick<Tables<"profiles">, "first_name" | "last_name"> | null;
};

export const PropertyReviews = ({ propertyId }: PropertyReviewsProps) => {
  const { toast } = useToast();
  const [showReviewForm, setShowReviewForm] = useState(false);

  const { data: reviews, isLoading } = useQuery({
    queryKey: ["property-reviews", propertyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("property_reviews")
        .select(`
          *,
          profiles(
            first_name,
            last_name
          )
        `)
        .eq("property_id", propertyId)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Could not load reviews",
          variant: "destructive",
        });
        throw error;
      }

      return data as unknown as ReviewWithUser[];
    },
  });

  const handleHelpfulVote = async (reviewId: string) => {
    const { error } = await supabase
      .from("property_reviews")
      .update({ helpful_votes: (reviews?.find(r => r.id === reviewId)?.helpful_votes || 0) + 1 })
      .eq("id", reviewId);

    if (error) {
      toast({
        title: "Error",
        description: "Could not update helpful votes",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Thank you for your feedback!",
    });
  };

  if (isLoading) {
    return <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Reviews</h2>
        <Button onClick={() => setShowReviewForm(true)} variant="outline">
          Write a Review
        </Button>
      </div>

      <ReviewList reviews={reviews || []} onHelpfulVote={handleHelpfulVote} />
    </div>
  );
};